import React from 'react';
import styled, { css } from 'styled-components';
import {
    Anchor,
    Image,
    JSEventUtils,
    Logger,
    NavigationModule,
} from 'UI-Dash-Commons-UI';
import MenuSpec from "../../../types/MenuSpec";
import MenuSection from "../../../types/MenuSection";

const TAG = 'BottomLeftMenuComponent';

const CALIFORNIA_PRIVACY_CHOICES_ICON_ALT_TEXT = 'California Consumer Privacy Act (CCPA) Opt-Out Icon';
const CALIFORNIA_PRIVACY_CHOICES_ICON = require( `../images/menu/privacy-choices-opt-out-icon.svg` );

type BottomLeftMenuComponentProps = {
    bottomLeftMenuSpec: MenuSpec | null,
    isLeftSideMenuOpen: boolean,
    isLeftSideMenuPinnedOpen: boolean,
    onSectionActivated: ( sectionSpec: MenuSection ) => void
}

const BottomLeftMenuComponent: React.StatelessComponent<BottomLeftMenuComponentProps> = (
    {
        isLeftSideMenuOpen,
        isLeftSideMenuPinnedOpen,
        bottomLeftMenuSpec,
        onSectionActivated
    }
) =>
{
    // If there is no bottom menu
    if ( !bottomLeftMenuSpec || bottomLeftMenuSpec.menu.length === 0 )
    {
        // Render an empty wrapper
        return <FooterWrapper/>;
    }

    const menuTitle: string = bottomLeftMenuSpec.menu[ 0 ].displayName;
    const sections: Array<MenuSection> = bottomLeftMenuSpec.menu[ 0 ].children;
    const handleFooterLinkClick: ( event: React.MouseEvent<HTMLAnchorElement> ) => void = ( event: React.MouseEvent<HTMLAnchorElement> ) =>
    {
        JSEventUtils.stopEventPropagation( event );

        const sectionId: string = ( event.currentTarget as HTMLAnchorElement ).id;
        const sectionSpec: MenuSection | undefined = sections.find( ( sectionSpec: MenuSection ) => sectionSpec.id === sectionId );

        if ( sectionSpec )
        {
            Logger.debug( TAG, 'Section clicked: \'' + sectionId + '\'' );

            onSectionActivated( sectionSpec );
        }
    };

    return <FooterWrapper
        isLeftSideMenuOpen={isLeftSideMenuOpen}
        isLeftSideMenuPinnedOpen={isLeftSideMenuPinnedOpen}
    >

        <FooterMenuHeader>{menuTitle}</FooterMenuHeader>
        <FooterMenuList>
            {
                sections.sort(
                    ( a, b ) => ( a.sortOrder - b.sortOrder )
                ).map( ( sectionSpec ) =>
                {
                    // special case for one trust cookies settings: https://app.asana.com/0/837882251489382/1202214124500791
                    if ( sectionSpec.externalUrl === NavigationModule.constants.ONE_TRUST_SDK_URL )
                    {
                        return <FooterMenuItem key={sectionSpec.id}>
                            <NavigationModule.components.OneTrustSDKButton/>
                        </FooterMenuItem>;
                    }

                    // special case applied across UI application legal doc footers for icon to be rendered alongside
                    // privacy choices link: https://app.asana.com/0/268044616416399/1203395005560474
                    let FooterMenuLinkIconComponent;
                    if ( sectionSpec.externalUrl === NavigationModule.constants.CALIFORNIA_PRIVACY_CHOICES_URL )
                    {
                        FooterMenuLinkIconComponent =
                            <FooterMenuLinkIcon src={CALIFORNIA_PRIVACY_CHOICES_ICON}
                                                alt={CALIFORNIA_PRIVACY_CHOICES_ICON_ALT_TEXT}/>;
                    }

                    return <FooterMenuItem key={sectionSpec.id}>
                        <FooterMenuItemWrapper>
                            <FooterMenuLink
                                id={sectionSpec.id}
                                text={sectionSpec.displayName}
                                href={sectionSpec.externalUrl || '#'}
                                onClick={handleFooterLinkClick}
                            />
                            {FooterMenuLinkIconComponent}
                        </FooterMenuItemWrapper>
                    </FooterMenuItem>;
                } )
            }
        </FooterMenuList>
    </FooterWrapper>;
};

const FooterWrapper = styled<{ isLeftSideMenuOpen?: boolean, isLeftSideMenuPinnedOpen?: boolean }, 'div'>( 'div' )`
  width: 180px;

  display: none;
  display: ${props => props.isLeftSideMenuPinnedOpen && 'block'};

  ${props => props.isLeftSideMenuPinnedOpen && FooterInPinnedState};
  ${props => !props.isLeftSideMenuPinnedOpen && FooterInUnpinnedState};

  opacity: 1;
  opacity: ${props => !props.isLeftSideMenuOpen && 0};
  transition-delay: 0.2s;
  transition: opacity 0.33s linear;
`;

const FooterInPinnedState = css`
  position: relative;
  margin-top: auto;
`;

const FooterInUnpinnedState = css`
  position: fixed;
  bottom: 0;
`;

const FooterMenuHeader = styled.div`
  margin-top: 1.5rem;
  margin-bottom: 1rem;
  padding-left: 1rem;
  
  color: #B8BABC;
  
  font-family: ${props => props.theme.fonts.regular};
  font-size: 12px;
`;

const FooterMenuList = styled.ul`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  justify-content: left;

  margin: 10px 0 10px 0;
  padding-left: 1rem;

  list-style: none;
`;

const FooterMenuItem = styled.li`
  display: inline;
  margin-bottom: 15px;
  margin-right: 10px;
`;

const FooterMenuLink = styled( Anchor )`
  color: ${props => props.theme.colors.footerLinkGray};
  text-decoration: none;

  font-family: ${props => props.theme.fonts.regular};
  font-size: 11px;
`;

const FooterMenuItemWrapper = styled.span`
  display: flex;
    
  width: max-content;
`;

const FooterMenuLinkIcon = styled( Image )`
  width: 100%;
  max-width: 26px;
  max-height: 42px;
  margin-left: 3px;
`;

export default BottomLeftMenuComponent;

